import ArticleLayout from '../../components/ArticleLayout';
import TreTjekFigure from '../../components/figures/TreTjekFigure';

export default function RadiatorVarmerIkkeEfterUdluftning() {
  return (
    <ArticleLayout
      title="Luftet ud, men stadig kold: De næste tre ting du skal tjekke"
      metaTitle="Radiator varmer ikke efter udluftning? Tjek disse 3 ting"
      metaDescription="Har du udluftet radiatoren uden held? Se de 3 næste trin: fastgroet ventilstift, faldet anlægstryk efter udluftning og blokering i ventilen."
      canonicalPath="/radiator-varmer-ikke-efter-udluftning"
      introLead="Når en radiator forbliver kold trods en grundig udluftning, skyldes det, at udluftning kun løser luftproblemer – ikke problemer med vandtilførslen."
      introAnswer="Når der kommer rent vand ud af luftskruen, er der ingen luftlomme i radiatorkroppen. De næste tre årsager er en fastgroet ventilstift i termostaten, for lavt anlægstryk på centralvarmeanlægget eller en defekt termostatføler."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: De næste 3 trin' },
        { id: 'stift', title: '2. Tjek ventilstiften bag termostathovedet' },
        { id: 'tryk', title: '3. Efterfyldning af vand efter udluftning' },
        { id: 'forindstilling', title: '4. Ventilens forindstilling og føler' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: De næste 3 trin efter forgæves udluftning"
      checklist={[
        {
          title: 'Afmontér termostathovedet og massér ventilstiften',
          description: 'Tag termostaten af og tryk stiften gentagne gange ind for at sikre, at den fjedrer helt ud i åben stilling.',
        },
        {
          title: 'Tjek anlæggets manometer og efterfyld vand om nødvendigt',
          description: 'Hver udluftning lukker vand og tryk ud af systemet; kontrollér at manometeret står på mindst 1,2–1,5 bar i et parcelhus.',
        },
        {
          title: 'Kontrollér om fremløbsrøret overhovedet er varmt',
          description: 'Mærk på røret ca. 30 cm før radiatoren; er røret iskoldt, ligger fejlen i strengen eller cirkulationspumpen.',
        },
      ]}
      diagram={<TreTjekFigure />}
      sections={[
        {
          id: 'stift',
          title: 'Trin 1: Ventilstiften sidder fast i lukket leje',
          content: (
            <>
              <p>
                Udluftning sikrer, at der er plads til vand i toppen af radiatoren. Men hvis termostatventilen er spærret, kan det varme vand ikke komme ind.
              </p>
              <p>
                Når termostathovedet skrues af, ses en lille stålstift på 2–3 mm. Denne stift trykker på en gummikegle inde i ventilen. Hvis stiften sidder i bund, er ventilen 100 % lukket. Tryk forsigtigt på stiften med en flad genstand, fx siden af en skruetrækker eller en mønt. Stiften skal let kunne trykkes ca. 2–3 mm ind og selv springe ud igen. Hvis den sidder urokkeligt fast, kan man banke let på siden af selve messingventilhuset – slå aldrig direkte på selve stiften.
              </p>
            </>
          ),
        },
        {
          id: 'tryk',
          title: 'Trin 2: Anlægstrykket er faldet efter udluftning',
          content: (
            <>
              <p>
                Et lukket centralvarmesystem indeholder en begrænset vandmængde. Når man åbner luftskruer på flere radiatorer, forsvinder der både luft og en smule vand.
              </p>
              <p>
                I et toetagers parcelhus skal anlægstrykket typisk være omkring <span className="font-mono">1,5 bar</span> (eller <span className="font-mono">0,15 MPa</span>). Falder trykket til under <span className="font-mono">1,0 bar</span>, kan cirkulationspumpen ikke overvinde højdeforskellen, og de øverste radiatorer stopper med at varme, uanset hvor meget man lufter ud.
              </p>
            </>
          ),
        },
        {
          id: 'forindstilling',
          title: 'Trin 3: Defekt termostathoved eller blokeret forindstilling',
          content: (
            <>
              <p>
                Moderne radiatorventiler har en integreret forindstilling (en ring med tal eller bogstaver under termostathovedet), der begrænser den maksimale vandgennemstrømning for at sikre hydraulisk balance.
              </p>
              <p>
                Hvis denne forindstilling ved en fejl er sat til det laveste trin, eller hvis der har ophobet sig snavs i dysen, vil radiatoren kun få en brøkdel af den nødvendige vandmængde. Prøv også at lade radiatoren køre uden termostathoved monteret i en halv time. Bliver radiatoren varm uden termostaten, er selve termostathovedets bælg defekt og skal udskiftes.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal du have VVS-hjælp?',
        content: (
          <p>
            Hvis stiften er løsnet, trykket er i orden og radiatoren stadig er iskold selv uden termostathoved monteret, kan ventilsædet være knækket indvendigt, eller returkoblingen i bunden kan være lukket. Kontakt en autoriseret VVS-installatør til at udskifte ventilen.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Der kom vand ud ved udluftning, men radiatoren forbliver kold – hvorfor?',
          a: 'Vand ved luftskruen viser kun, at radiatoren er fuld af vand. Det betyder ikke, at vandet cirkulerer. Hvis ventilstiften sidder fast, eller returventilen er lukket, står vandet stille og køler af.',
        },
        {
          q: 'Må jeg trække i ventilstiften med en tang?',
          a: 'Nej, træk aldrig i stiften med en tang. Hvis du trækker stiften ud af pakdåsen, kan du ødelægge pakningen og risikere en vandskade. Bank i stedet forsigtigt på siden af ventilhuset.',
        },
        {
          q: 'Skal der fyldes vand på varmeanlægget efter udluftning?',
          a: 'Ja, hvis du har et lukket anlæg (eget fyr eller indirekte fjernvarmeunit), bør du altid tjekke manometeret efter udluftning og efterfylde til ca. 1,5 bar ved koldt anlæg.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'den overordnede tjekliste' },
        { to: '/radiator-ventil-sidder-fast', text: 'Ventilstiften sidder fast', desc: 'detaljeret guide til at løsne stiften' },
        { to: '/udluftning-af-radiator', text: 'Sådan lufter du en radiator ud', desc: 'trin for trin guide med luftnøgle' },
        { to: '/radiator-tryk', text: 'Vandtrykket i anlægget', desc: 'kontrol af manometer og påfyldning' },
      ]}
      sources={[
        { url: 'https://www.hofor.dk/erhverv/fjernvarme/spoergsmaal-svar-fjernvarme-erhverv/fjernvarmen-fungerer-ejendommens-radiatorer-ikke-varme-goer-vi/', label: 'Fjernvarmen fungerer, men ejendommens radiatorer er ikke varme - HOFOR' },
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/sommerluk-varmeanlaegget', label: 'Sommerluk varmeanlægget og spar 500 kr. på 5 min | SparEnergi' },
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/saadan-faar-du-mest-ud-af-fjernvarmen', label: 'Sådan får du mest ud af fjernvarme | SparEnergi' },
      ]}
    />
  );
}
